import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { DatePicker } from 'Components';
import {
    Table,
    Typography,
    Modal,
    Button,
    Form,
    InputNumber,
    Input,
    Select,
    Space,
    Row,
    Col,
    Descriptions,
    Card,
    Checkbox,
    Radio
} from 'antd';
import { deleteRequest, getRequest, patchRequest, postRequest, ResponseDataType } from 'api';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { FilterOutlined } from '@mui/icons-material';
const { Title } = Typography;

// Helper format/parse for currency with dot separators
const formatCurrency = (value?: string | number) => {
    if (value === undefined || value === null) return '';
    const str = String(value);
    return str.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const parseCurrency = (value?: string | number) => {
    if (value === undefined || value === null) return 0;
    return Number(String(value).replace(/\./g, '')) || 0;
};

interface StudentData {
    name: string;
    dob: string;
    gender: number;
    email: string;
    address: string;
    cmnd: string;
    bhytNum: string;
    studentCode: string;
}

interface ParentData {
    parentId: number;
    name: string;
    phoneNumber: string;
    email: string;
}

interface IncomeSourceData {
    id: number;
    description: string;
    unitPrice: number;
    expectedUnits: number;
    actualUnits: number;
    createdDate: number[];
    updatedDate: number[];
    incomeSchoolId: number;
    month: number;
    year: number;
    status: number;
    refundedAmount: number;
}

export const StudentFullInfo = ({ 
    studentId: propStudentId, 
    classYearId: propClassYearId, 
    status: propStatus,
    onStudentLoaded
}: { 
    studentId?: string | number, 
    classYearId?: string | number, 
    status?: number,
    onStudentLoaded?: (student: StudentData) => void
}) => {

    const { studentId: paramStudentId, classYearId: paramClassYearId } = useParams<{ studentId: string; classYearId: string }>();
    const studentId = (propStudentId || paramStudentId)?.toString();
    const classYearId = (propClassYearId || paramClassYearId)?.toString();
    const [searchParams] = useSearchParams();
    const statusParam = searchParams.get('status');
    const [statusFilter, setStatusFilter] = useState<number | null>(propStatus !== undefined ? propStatus : (statusParam !== null ? parseInt(statusParam) : 0));

    useEffect(() => {
        if (statusParam !== null) {
            setStatusFilter(parseInt(statusParam));
        }
    }, [statusParam]);
    const [incomeTypeMap, setIncomeTypeMap] = useState<Record<number, string>>({});
    const [selectedType, setSelectedType] = useState<number | null>(null);
    const [selectedIncomeType, setSelectedIncomeType] = useState<number | null>(null);
    const [totalAmount, setTotalAmount] = useState<number>(0);
    const [student, setStudent] = useState<StudentData | null>(null);
    const [parents, setParents] = useState<ParentData[]>([]);
    const [incomes, setIncomes] = useState<IncomeSourceData[]>([]);
    // const [classYearId, setClassYearId] = useState<number | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [form] = Form.useForm();
    const [invoiceform] = Form.useForm();
    const [selectedIncome, setSelectedIncome] = useState<IncomeSourceData | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editForm] = Form.useForm();
    const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
    const [ungroupedBills, setUngroupedBills] = useState<any[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [incomeSources, setIncomeSources] = useState<{ id: number; name: string }[]>([]);
    const schoolId = Number(localStorage.getItem("schoolId"));
    const [incomeSchoolMap, setIncomeSchoolMap] = useState<Record<number, { name: string; type: number }>>({});
    const [incomeNameMap, setIncomeNameMap] = useState<Record<number, string>>({});
    const [addPeriodType, setAddPeriodType] = useState<'month' | 'year'>('month');
    const [editPeriodType, setEditPeriodType] = useState<'month' | 'year'>('month');

    const handleOpenAddModal = () => {
        setAddPeriodType('month');
        form.resetFields();
        form.setFieldsValue({
            periodType: 'month',
            monthYear: dayjs(),
            schoolYear: dayjs(),
        });
        setIsModalOpen(true);
    };

    const renderAddPeriodSelection = () => (
        <>
            <Form.Item label="Kỳ thu" style={{ marginBottom: 12 }}>
                <Radio.Group
                    value={addPeriodType}
                    onChange={(e) => {
                        const newType = e.target.value as 'month' | 'year';
                        setAddPeriodType(newType);
                        form.setFieldsValue({ periodType: newType });
                        const allValues = { ...form.getFieldsValue(), periodType: newType };
                        const name = allValues.incomeName || '';
                        let timeStr = '';
                        if (newType === 'year') {
                            const sYear = allValues.schoolYear || dayjs();
                            form.setFieldsValue({ schoolYear: sYear });
                            const y = sYear.year();
                            timeStr = ` Năm học ${y}-${y + 1}`;
                        } else {
                            const mYear = allValues.monthYear || dayjs();
                            form.setFieldsValue({ monthYear: mYear });
                            timeStr = ` ${mYear.format('MM/YYYY')}`;
                        }
                        if (name || timeStr) {
                            form.setFieldsValue({ description: `${name}${timeStr}`.trim() });
                        }
                    }}
                >
                    <Radio.Button value="month">Theo Tháng/Năm</Radio.Button>
                    <Radio.Button value="year">Theo Năm học</Radio.Button>
                </Radio.Group>
            </Form.Item>

            {addPeriodType === 'month' ? (
                <Form.Item name="monthYear" label="Tháng/Năm" rules={[{ required: true, message: 'Vui lòng chọn tháng/năm' }]}>
                    <DatePicker picker="month" style={{ width: '100%' }} placeholder="Chọn tháng/năm" format="MM/YYYY" />
                </Form.Item>
            ) : (
                <Form.Item name="schoolYear" label="Năm học" rules={[{ required: true, message: 'Vui lòng chọn năm học' }]}>
                    <DatePicker
                        picker="year"
                        style={{ width: '100%' }}
                        placeholder="Chọn năm học"
                        format={(val) => `${val.year()}-${val.year() + 1}`}
                    />
                </Form.Item>
            )}
        </>
    );


    const formatDate = (dateArr: number[]) => {
        if (!dateArr || dateArr.length < 3) return '';
        const [year, month, day, hour = 0, minute = 0] = dateArr;
        return `${day.toString().padStart(2, '0')}/${month.toString().padStart(2, '0')}/${year} ${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
    };

    const fetchData = async () => {
        if (!studentId || !classYearId) return;
        try {
            const studentRes: ResponseDataType<StudentData> = await getRequest(`/cms/student/${studentId}`);
            if (studentRes?.code !== 200 || !studentRes.info) {
                toast.error("Không thể tải thông tin học sinh");
                return;
            }
            setStudent(studentRes.info);
            if (onStudentLoaded) {
                onStudentLoaded(studentRes.info);
            }

            // Gọi API lấy studentClassYearId
            const studentClassYearRes: ResponseDataType<number> = await getRequest(
                `/cms/class/get-student-class-year-by-student-and-class-year?classYearId=${classYearId}&studentId=${studentId}`
            );

            if (studentClassYearRes.code !== 200 || !studentClassYearRes.info) {
                toast.error("Không tìm thấy studentClassYearId");
                return;
            }

            const studentClassYearId = studentClassYearRes.info;

            const [parentsRes, incomesRes] = await Promise.all([
                getRequest(`/cms/student/get-parent-by-student?studentId=${studentId}`),
                getRequest(
                    `/cms/accountant/income-student-register/student-class-year-and-status?studentClassYearId=${studentClassYearId}` +
                    (statusFilter !== null ? `&status=${statusFilter}` : '')
                ),
            ]);
            if (parentsRes?.code === 200 && Array.isArray(parentsRes.info)) {
                setParents(parentsRes.info);
            } else {
                toast.error("Không thể tải danh sách phụ huynh");
            }

            if (incomesRes?.code === 200 && Array.isArray(incomesRes.info?.list)) {
                setIncomes(incomesRes.info.list);
            } else {
                toast.error("Không thể tải nguồn thu");
            }

        } catch (error) {
            toast.error("Đã xảy ra lỗi khi tải dữ liệu");
            console.error("Lỗi khi gọi API:", error);
        }
    };
    useEffect(() => {
        const fetchIncomeNames = async () => {
            const uniqueIds = Array.from(new Set(incomes.map(item => item.incomeSchoolId)));

            const newMap: Record<number, string> = {};
            await Promise.all(uniqueIds.map(async (incomeSchoolId) => {
                try {
                    const res = await getRequest(`/cms/accountant/income-school/${incomeSchoolId}`);
                    if (res.code === 200 && res.info?.name) {
                        newMap[incomeSchoolId] = res.info.name;
                    }
                } catch {
                    newMap[incomeSchoolId] = 'Không xác định';
                }
            }));

            setIncomeNameMap(newMap);
        };

        if (incomes.length > 0) {
            fetchIncomeNames();
        }
    }, [incomes]);
    const calculateTotal = (selectedKeys: React.Key[]) => {
        const selectedBills = ungroupedBills.filter((bill) =>
            selectedKeys.includes(bill.id)
        );

        const total = selectedBills.reduce((sum, bill) => {
            const { incomeSchoolId, unitPrice, expectedUnits, refundedAmount } = bill;
            const type = incomeSchoolMap[incomeSchoolId]?.type;
            if (!type || !unitPrice) return sum;

            let subtotal = 0;
            switch (type) {
                case 1:
                case 3:
                    subtotal = unitPrice * expectedUnits;
                    break;
                case 2:
                case 4:
                case 5:
                    subtotal = unitPrice;
                    break;
                default: return sum;
            }

            const refunded = refundedAmount ?? 0;
            return sum + (subtotal - refunded);
        }, 0);

        setTotalAmount(total);
    };


    useEffect(() => {
        const fetchIncomeSchoolInfo = async () => {
            const uniqueIds = Array.from(new Set(incomes.map(item => item.incomeSchoolId)));
            const newMap: Record<number, { name: string; type: number }> = {};

            for (const id of uniqueIds) {
                try {
                    const res = await getRequest(`/cms/accountant/income-school/${id}`);
                    if (res.code === 200 && res.info) {
                        newMap[id] = {
                            name: res.info.name,
                            type: res.info.type,
                        };
                    }
                } catch (err) {
                    newMap[id] = { name: 'Không xác định', type: -1 };
                }
            }

            setIncomeSchoolMap(newMap);
        };

        if (incomes.length > 0) {
            fetchIncomeSchoolInfo();
        }
    }, [incomes]);
    useEffect(() => {
        const fetchIncomeTypes = async () => {
            const uniqueIds = Array.from(new Set(incomes.map(item => item.incomeSchoolId)));

            const map: Record<number, string> = {};

            await Promise.all(
                uniqueIds.map(async (id) => {
                    try {
                        const res = await getRequest(`/cms/accountant/income-school/${id}`);
                        if (res.code === 200 && res.info?.name) {
                            map[id] = res.info.name;
                        } else {
                            map[id] = 'Không xác định';
                        }
                    } catch (err) {
                        map[id] = 'Không xác định';
                    }
                })
            );

            setIncomeTypeMap(map);
        };

        if (incomes.length > 0) {
            fetchIncomeTypes();
        }
    }, [incomes]);

    useEffect(() => {
        fetchData();

    }, [studentId, statusFilter]);

    const sortedIncomes = incomes.slice().sort((a, b) => {
        if (b.year !== a.year) {
            return b.year - a.year; // Năm mới hơn lên trước
        }
        return (b.month ?? 0) - (a.month ?? 0); // Nếu cùng năm thì tháng mới hơn lên trước
    });



    function parseCreatedDate(date: number[]): Date {
        if (!Array.isArray(date) || date.length < 3) return new Date(0); // fallback
        const [year, month, day, hour = 0, minute = 0, second = 0] = date;
        return new Date(year, month - 1, day, hour, minute, second); // month - 1 vì JS tính từ 0
    }


    const handleAddIncome = async () => {
        try {
            const values = await form.validateFields();
            if (!studentId || !classYearId) return;


            const studentClassYearRes: ResponseDataType<number> = await getRequest(
                `/cms/class/get-student-class-year-by-student-and-class-year?classYearId=${classYearId}&studentId=${studentId}`
            );

            if (studentClassYearRes.code !== 200 || !studentClassYearRes.info) {
                toast.error("Không tìm thấy studentClassYearId");
                return;
            }

            const studentClassYearId = studentClassYearRes.info;

            let month = 0;
            let year = dayjs().year();

            if (addPeriodType === 'year' || values.periodType === 'year') {
                if (!values.schoolYear) {
                    toast.error("Vui lòng chọn năm học");
                    return;
                }
                month = 0;
                year = values.schoolYear.year();
            } else {
                if (!values.monthYear) {
                    toast.error("Vui lòng chọn tháng/năm");
                    return;
                }
                month = values.monthYear.month() + 1;
                year = values.monthYear.year();
            }

            let isDuplicate = false;
            try {
                const checkRes: any = await getRequest(
                    `/cms/accountant/income-student-register/student-class-year-and-status?studentClassYearId=${studentClassYearId}`
                );
                if (checkRes.code === 200 && Array.isArray(checkRes.info?.list)) {
                    isDuplicate = checkRes.info.list.some((inc: any) => 
                        inc.incomeSchoolId === values.incomeSchoolId &&
                        inc.month === month &&
                        inc.year === year
                    );
                }
            } catch (e) {
                // ignore
            }

            const submitPayload = async () => {
                const payload = {
                    ...values,
                    month,
                    year,
                    actualUnits: values.expectedUnits,
                    status: 0,
                };
                const res = await postRequest(
                    `/cms/accountant/income-student-register/list-student-class-years?studentClassYearIds=${studentClassYearId}`,
                    payload
                );

                if (res.code === 200) {
                    toast.success("Thêm nguồn thu thành công");
                    setIsModalOpen(false);
                    form.resetFields();
                    fetchData(); // Refresh lại danh sách nguồn thu
                } else {
                    toast.error("Thêm nguồn thu thất bại");
                }
            };

            if (isDuplicate) {
                Modal.confirm({
                    title: 'Xác nhận tạo thêm',
                    content: 'Khoản thu này đã tồn tại trong kỳ thu tương ứng. Bạn có chắc chắn muốn tiếp tục tạo thêm bản ghi thứ hai không?',
                    okText: 'Đồng ý',
                    cancelText: 'Hủy',
                    onOk: submitPayload,
                });
                return;
            }

            await submitPayload();

        } catch (error) {
            toast.error("Vui lòng kiểm tra lại thông tin");
        }
    };
    const handleIncomeTypeChange = (value: number) => {
        setSelectedType(value);
        setSelectedIncomeType(value);
        setIncomeSources([]);
        form.setFieldsValue({
            type: value,
            incomeName: undefined,
            incomeSchoolId: undefined,
        });
        handleTypeChange(value); // ⬅️ RẤT QUAN TRỌNG
    };
    const handleTypeChange = async (type: number) => {
        setIncomeSources([]);
        try {
            const res = await getRequest(`/cms/accountant/income-school/get-income-school-by-school-and-type?schoolId=${schoolId}&type=${type}`);
            if (res.code === 200 && Array.isArray(res.info)) {
                setIncomeSources(res.info.map((item: any) => ({ id: item.id, name: item.name })));
            } else {
                setIncomeSources([]);
                toast.error("Không thể tải danh sách tên nguồn thu");
            }
        } catch (e) {
            setIncomeSources([]);
            toast.error("Lỗi khi tải tên nguồn thu");
        }
    };
    const handleNameChange = (name: string) => {
        const source = incomeSources.find((s) => s.name === name);
        if (source) {
            form.setFieldsValue({ incomeSchoolId: source.id });
        }
    };

    const fetchUngroupedBills = async () => {
        try {
            const studentClassYearRes: ResponseDataType<number> = await getRequest(
                `/cms/class/get-student-class-year-by-student-and-class-year?classYearId=${classYearId}&studentId=${studentId}`
            );
            if (studentClassYearRes.code !== 200) {
                toast.error("Không lấy được studentClassYearId");
                return;
            }

            const res = await getRequest(
                `/cms/accountant/income-student-register/student-class-year-and-status?studentClassYearId=${studentClassYearRes.info}&status=0`
            );

            if (res.code === 200 && Array.isArray(res.info?.list)) {
                setUngroupedBills(res.info.list);
                setIsInvoiceModalOpen(true);
            } else {
                toast.error("Không có bill chưa gộp nào");
            }
        } catch (err) {
            toast.error("Lỗi khi tải danh sách bill chưa gộp");
        }
    };
    const handleGroupInvoice = async () => {
        console.log("Bắt đầu gộp hóa đơn"); // Test

        if (selectedRowKeys.length === 0) {
            toast.warn("Vui lòng chọn ít nhất 1 bill để gộp");
            return;
        }

        try {
            await invoiceform.validateFields();

            const content = invoiceform.getFieldValue("content")?.trim();
            const queryParams = new URLSearchParams();
            selectedRowKeys.forEach((id) => queryParams.append("ids", id.toString()));
            queryParams.append("content", content);

            const res = await postRequest(
                `/cms/accountant/income-student-register/group/invoice?${queryParams.toString()}`,
                {}
            );

            if (res.code === 200) {
                toast.success("Gộp hóa đơn thành công");
                setIsInvoiceModalOpen(false);
                setSelectedRowKeys([]);
                invoiceform.resetFields();
                fetchData();
            } else {
                toast.error(res.msg || "Gộp hóa đơn thất bại");
            }
        } catch (error) {
            console.log("🧪 validateFields ERROR:", error);
            if ((error as any)?.errorFields) {
                toast.error("Vui lòng kiểm tra lại nội dung gộp");
                return;
            }
            toast.error("Lỗi khi gộp hóa đơn");
        }

    };


    return (
        <div>
            <Row gutter={24}>
            </Row>

            <Card style={{ marginTop: 24 }}>

                <Row justify="space-between" align="middle" style={{ marginTop: 5, marginBottom: 24 }}>
                    <Col>
                        <Space>
                            <Title level={4} style={{ margin: 0 }}>Khoản thu</Title>
                            <Select
                                placeholder="Lọc theo trạng thái"
                                value={statusFilter}
                                onChange={(value) => setStatusFilter(value)}
                                style={{ width: 200 }}
                                allowClear
                            ><Select.Option value={null}>Tất cả bill </Select.Option>
                                <Select.Option value={0}>Bill chưa gộp </Select.Option>
                                <Select.Option value={1}>Bill đã gộp </Select.Option>
                            </Select>
                        </Space>
                    </Col>

                    <Col>
                        <Space>
                            <Button type="primary" onClick={fetchUngroupedBills}>
                                Tạo invoice
                            </Button>
                            <Button type="primary" onClick={handleOpenAddModal}>
                                Đăng ký khoản thu
                            </Button>
                        </Space>
                    </Col>
                </Row>

                <Modal
                    title="Chọn bill để gộp hóa đơn"
                    open={isInvoiceModalOpen}
                    onCancel={() => setIsInvoiceModalOpen(false)}
                    onOk={() => {
                        console.log("Modal OK clicked"); // Test
                        handleGroupInvoice();
                    }}
                    okText="Gộp"
                    cancelText="Hủy"
                    width="90vw"
                    style={{ maxWidth: 1350, top: 20 }}
                >
                    <Form form={invoiceform} layout="vertical">
                        <Form.Item
                            name="content"
                            label="Nội dung gộp hóa đơn"
                            rules={[{ required: true, message: "Vui lòng nhập nội dung" }]}
                        >
                            <Input.TextArea rows={2} placeholder="Nhập lý do hoặc ghi chú khi gộp hóa đơn..." />
                        </Form.Item>

                        <Table
                            rowKey={(record) => record.id.toString()}
                            rowSelection={{
                                onChange: (newKeys, selectedRows) => {
                                    setSelectedRowKeys(newKeys);

                                    // Tính tổng tiền thanh toán từ các row được chọn
                                    let sum = 0;
                                    let contentDesc: string[] = [];
                                    selectedRows.forEach((record) => {
                                        const { incomeSchoolId, unitPrice, expectedUnits, refundedAmount, description } = record;
                                        const type = incomeSchoolMap[incomeSchoolId]?.type;
                                        
                                        if (description) {
                                            contentDesc.push(description);
                                        }

                                        if (!type || !unitPrice) return;

                                        let total = 0;
                                        switch (type) {
                                            case 1: // ăn uống
                                            case 3: // học theo buổi
                                                total = unitPrice * expectedUnits;
                                                break;
                                            case 2: // học phí
                                            case 4: // chi phí khác
                                            case 5: // thu hộ
                                                total = unitPrice;
                                                break;
                                            default:
                                                return;
                                        }

                                        const refunded = refundedAmount ?? 0;
                                        sum += total - refunded;
                                    });
                                    
                                    invoiceform.setFieldsValue({ content: contentDesc.join(', ') });
                                    setTotalAmount(sum);
                                },
                            }}
                            dataSource={ungroupedBills}
                            pagination={false}
                            columns={[
                                {
                                    title: 'Tên nguồn thu',
                                    dataIndex: 'incomeSchoolId',
                                    render: (id: number) => incomeTypeMap[id] || 'Không xác định',
                                },
                                { title: 'Mô tả', dataIndex: 'description' },
                                {
                                    title: 'Đơn giá',
                                    dataIndex: 'unitPrice',
                                    align: 'right',
                                    render: (val) => `${val.toLocaleString()} đ`,
                                },
                                { title: 'Số buổi dự kiến', dataIndex: 'expectedUnits', align: 'center' },
                                // { title: 'Số buổi thực tế', dataIndex: 'actualUnits' },

                                {
                                    title: 'Thời gian',
                                    align: 'center',
                                    render: (_, record) => (!record.month || record.month === 0) ? `Năm học ${record.year}-${record.year + 1}` : `${record.month}/${record.year}`,
                                },
                                {
                                    title: 'Thành tiền',
                                    key: 'totalAmount',
                                    align: 'right',
                                    render: (record) => {
                                        const { incomeSchoolId, unitPrice, expectedUnits } = record;
                                        const type = incomeSchoolMap[incomeSchoolId]?.type;

                                        if (!type || !unitPrice) return 'Không xác định';

                                        switch (type) {
                                            case 1:
                                            case 3:
                                                return `${(unitPrice * expectedUnits).toLocaleString()} đ`;
                                            case 2:
                                            case 4:
                                            case 5:
                                                return `${unitPrice.toLocaleString()} đ`;
                                            default:
                                                return 'Không xác định';
                                        }
                                    }
                                },
                                {
                                    title: 'Tiền tồn',
                                    dataIndex: 'refundedAmount',
                                    align: 'right',
                                    render: val => (typeof val === 'number' ? `${val.toLocaleString()} đ` : ''),
                                },
                                {
                                    title: 'Tổng tiền thanh toán',
                                    key: 'finalAmount',
                                    align: 'right',
                                    render: (record) => {
                                        const { incomeSchoolId, unitPrice, expectedUnits, refundedAmount } = record;
                                        const type = incomeSchoolMap[incomeSchoolId]?.type;

                                        if (!type || !unitPrice) return 'Không xác định';

                                        let total = 0;
                                        switch (type) {
                                            case 1:
                                            case 3:
                                                total = unitPrice * expectedUnits;
                                                break;
                                            case 2:
                                            case 4:
                                            case 5:
                                                total = unitPrice;
                                                break;
                                            default:
                                                return 'Không xác định';
                                        }

                                        const refunded = refundedAmount ?? 0;
                                        return `${(total - refunded).toLocaleString()} đ`;
                                    }
                                },
                            ]}
                        />

                        <div style={{ textAlign: "right", marginTop: 14, fontSize: '15px' }}>
                            Tổng tiền các bill đã chọn: <b style={{ color: '#f5222d', fontSize: '16px', marginLeft: 8 }}>{totalAmount.toLocaleString()} đ</b>
                        </div>
                    </Form>
                </Modal>





                <Table
                    dataSource={sortedIncomes}
                    pagination={false}
                    columns={[
                        {
                            title: 'Loại nguồn thu',
                            dataIndex: 'incomeSchoolId',
                            //   filteredValue: filteredType !== null ? [filteredType] : null,
                            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                                <div style={{ padding: 2 }}>
                                    {[
                                        { text: 'Nhóm ăn uống', value: 1 },
                                        { text: 'Nhóm học phí', value: 2 },
                                        { text: 'Nhóm học theo buổi (tiết)', value: 3 },
                                        { text: 'Nhóm chi phí khác', value: 4 },
                                        { text: 'Nhóm thu hộ', value: 5 },
                                    ].map(({ text, value }) => (
                                        <div key={value}>
                                            <label style={{ display: 'block' }}>
                                                <input
                                                    type="radio"
                                                    value={value}
                                                    checked={selectedKeys.includes(value)}
                                                    onChange={() => setSelectedKeys([value])}
                                                    style={{ marginRight: 8 }}
                                                />
                                                {text}
                                            </label>
                                        </div>
                                    ))}
                                    <div style={{ marginTop: 8 }}>
                                        <Button
                                            onClick={() => {
                                                clearFilters?.();
                                                confirm();
                                            }}
                                            size="small"
                                            style={{ marginRight: 8 }}
                                        >
                                            Reset
                                        </Button>
                                        <Button
                                            type="primary"
                                            onClick={() => confirm()}
                                            size="small"
                                        >
                                            OK
                                        </Button>
                                    </div>
                                </div>
                            ),

                            onFilter: (value, record) => {
                                const incomeSchool = incomeSchoolMap[record.incomeSchoolId];
                                return incomeSchool?.type === value;
                            },
                            render: (id: number) => {
                                const type = incomeSchoolMap[id]?.type;
                                const typeLabelMap: Record<number, string> = {
                                    1: 'Nhóm ăn uống',
                                    2: 'Nhóm học phí',
                                    3: 'Nhóm học theo buổi (tiết)',
                                    4: 'Nhóm chi phí khác',
                                    5: 'Nhóm thu hộ',
                                };
                                return typeLabelMap[type ?? -1] || 'Không xác định';
                            },
                        },


                        {
                            title: 'Tên nguồn thu',
                            dataIndex: 'incomeSchoolId',
                            render: (id: number) => incomeNameMap[id] || 'Đang tải...',
                        },
                        {
                            title: 'Mô tả',
                            dataIndex: 'description',

                        },
                        {
                            title: 'Đơn giá',
                            dataIndex: 'unitPrice',
                            render: (val) => `${val.toLocaleString()} đ`,
                        },
                        {
                            title: 'Số buổi dự kiến',
                            dataIndex: 'expectedUnits',
                        },
                        {
                            title: 'Số buổi thực tế',
                            dataIndex: 'actualUnits',
                        },
                        {
                            title: 'Thành tiền',
                            key: 'totalAmount',
                            render: (record) => {
                                const { incomeSchoolId, unitPrice, expectedUnits } = record;
                                const type = incomeSchoolMap[incomeSchoolId]?.type;

                                if (!type || !unitPrice) return 'Không xác định';

                                switch (type) {
                                    case 1: // Nhóm ăn uống
                                    case 3: // Nhóm học theo buổi (tiết)
                                        return `${(unitPrice * expectedUnits).toLocaleString()} đ`;
                                    case 2: // Nhóm học phí
                                    case 4: // Nhóm chi phí khác
                                    case 5: // Nhóm thu hộ
                                        return `${unitPrice.toLocaleString()} đ`;
                                    default:
                                        return 'Không xác định';
                                }
                            }
                        },

                        {
                            title: 'Tháng-năm',
                            render: (_, record) => {
                                const { month, year } = record;
                                if (!year) return '';
                                if (!month || month === 0) return `Năm học ${year}-${year + 1}`;
                                return `${month.toString().padStart(2, '0')}/${year}`;
                            },
                        },


                        {
                            title: 'Hành động',
                            key: 'action',
                            render: (_, record) => (
                                <Space>
                                    <Button
                                        type="link"
                                        onClick={() => {
                                            setSelectedIncome(record);
                                            const isYear = !record.month || record.month === 0;
                                            setEditPeriodType(isYear ? 'year' : 'month');
                                            setIsEditModalOpen(true);
                                            editForm.setFieldsValue({
                                                ...record,
                                                periodType: isYear ? 'year' : 'month',
                                                schoolYear: isYear ? dayjs().year(record.year) : dayjs(),
                                                monthYear: !isYear ? dayjs(`${record.year}-${record.month}`, 'YYYY-M') : dayjs(),
                                            });
                                        }}

                                    >
                                        Cập nhật
                                    </Button>
                                    <Button
                                        type="link"
                                        danger
                                        onClick={async () => {
                                            Modal.confirm({
                                                title: 'Xác nhận xóa',
                                                content: 'Bạn có chắc chắn muốn xóa khoản thu này?',
                                                okText: 'Xóa',
                                                cancelText: 'Hủy',
                                                okButtonProps: { danger: true },
                                                onOk: async () => {
                                                    try {
                                                        const data = await deleteRequest(`/cms/accountant/income-student-register/${record.id}`);

                                                        if (data.code === 200) {
                                                            toast.success("Xóa thành công");
                                                            await fetchData(); // reload bảng
                                                        } else {
                                                            toast.error(data.msg || "Xóa thất bại");
                                                        }
                                                    } catch (err) {
                                                        toast.error("Đã xảy ra lỗi khi xóa");
                                                    }
                                                }

                                            });
                                        }}
                                    >
                                        Xóa
                                    </Button>
                                </Space>
                            ),
                        }

                    ]}

                    rowKey={(record) => record.id.toString()}
                />
            </Card>
            <Modal
                title="Cập nhật nguồn thu"
                open={isEditModalOpen}
                onCancel={() => setIsEditModalOpen(false)}
                onOk={async () => {
                    try {
                        const values = await editForm.validateFields();

                        let month = 0;
                        let year = dayjs().year();

                        if (editPeriodType === 'year' || values.periodType === 'year') {
                            if (!values.schoolYear) {
                                toast.error("Vui lòng chọn năm học");
                                return;
                            }
                            month = 0;
                            year = values.schoolYear.year();
                        } else {
                            if (!values.monthYear) {
                                toast.error("Vui lòng chọn tháng/năm");
                                return;
                            }
                            month = values.monthYear.month() + 1;
                            year = values.monthYear.year();
                        }

                        const currentIncomeSchoolId = values.incomeSchoolId || selectedIncome?.incomeSchoolId;
                        let isDuplicate = false;
                        
                        try {
                            const studentClassYearRes: any = await getRequest(
                                `/cms/class/get-student-class-year-by-student-and-class-year?classYearId=${classYearId}&studentId=${studentId}`
                            );
                            if (studentClassYearRes.code === 200 && studentClassYearRes.info) {
                                const scyId = studentClassYearRes.info;
                                const checkRes: any = await getRequest(
                                    `/cms/accountant/income-student-register/student-class-year-and-status?studentClassYearId=${scyId}`
                                );
                                if (checkRes.code === 200 && Array.isArray(checkRes.info?.list)) {
                                    isDuplicate = checkRes.info.list.some((inc: any) => 
                                        inc.id !== selectedIncome?.id &&
                                        inc.incomeSchoolId === currentIncomeSchoolId &&
                                        inc.month === month &&
                                        inc.year === year
                                    );
                                }
                            }
                        } catch (e) {
                            // ignore
                        }

                        const submitEdit = async () => {
                            const payload = {
                                ...selectedIncome, // giữ lại các thông tin cũ (có thể chứa id)
                                ...values,
                                month,
                                year,
                            };

                            const res = await patchRequest('/cms/accountant/income-student-register', payload);

                            if (res.code === 200) {
                                toast.success("Cập nhật nguồn thu thành công");
                                setIsEditModalOpen(false);
                                fetchData();
                            } else {
                                toast.error(res.msg || "Cập nhật nguồn thu thất bại");
                            }
                        };

                        if (isDuplicate) {
                            Modal.confirm({
                                title: 'Xác nhận lưu',
                                content: 'Bạn đang đổi sang kỳ thu đã có sẵn khoản thu này. Bạn có chắc chắn muốn lưu thành bản ghi trùng thứ hai không?',
                                okText: 'Đồng ý',
                                cancelText: 'Hủy',
                                onOk: submitEdit,
                            });
                            return;
                        }

                        await submitEdit();
                    } catch (error) {
                        toast.error("Vui lòng kiểm tra lại thông tin");
                    }
                }}
                okText="Cập nhật"
                cancelText="Hủy"
            >
                <Form layout="vertical" form={editForm}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="unitPrice"
                                label="Đơn giá"
                                rules={[{ required: true }]}
                            >
                                <InputNumber<number>
                                    style={{ width: '100%' }}
                                    min={0}
                                    formatter={(v) => formatCurrency(v as any)}
                                    parser={(v) => parseCurrency(v as any)}
                                    disabled={selectedIncome?.status === 1}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="expectedUnits"
                                label="Số buổi dự kiến"
                                rules={[{ required: true }]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    min={0}
                                    disabled={selectedIncome?.status === 1}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="actualUnits"
                                label="Số buổi thực tế"
                                rules={[{ required: true }]}
                            >
                                <InputNumber style={{ width: '100%' }} min={0} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Kỳ thu" style={{ marginBottom: 12 }}>
                                <Radio.Group
                                    value={editPeriodType}
                                    disabled={selectedIncome?.status === 1}
                                    onChange={(e) => {
                                        const newType = e.target.value as 'month' | 'year';
                                        setEditPeriodType(newType);
                                        editForm.setFieldsValue({ periodType: newType });
                                    }}
                                >
                                    <Radio.Button value="month">Tháng/Năm</Radio.Button>
                                    <Radio.Button value="year">Năm học</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            {editPeriodType === 'month' ? (
                                <Form.Item
                                    name="monthYear"
                                    label="Tháng/Năm"
                                    rules={[{ required: true, message: 'Vui lòng chọn tháng/năm' }]}
                                >
                                    <DatePicker
                                        picker="month"
                                        style={{ width: '100%' }}
                                        format="MM/YYYY"
                                        disabled={selectedIncome?.status === 1}
                                    />
                                </Form.Item>
                            ) : (
                                <Form.Item
                                    name="schoolYear"
                                    label="Năm học"
                                    rules={[{ required: true, message: 'Vui lòng chọn năm học' }]}
                                >
                                    <DatePicker
                                        picker="year"
                                        style={{ width: '100%' }}
                                        placeholder="Chọn năm học"
                                        format={(val) => `${val.year()}-${val.year() + 1}`}
                                        disabled={selectedIncome?.status === 1}
                                    />
                                </Form.Item>
                            )}
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="description"
                                label="Mô tả"
                                rules={[{ required: true }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>

            </Modal>

            {/* Modal thêm nguồn thu */}
            <Modal
                title="Thêm nguồn thu"
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onOk={handleAddIncome}
                okText="Thêm"
                cancelText="Hủy"
            >
                <Form layout="vertical" form={form} onValuesChange={(changedValues, allValues) => {
                    if (changedValues.incomeName || changedValues.monthYear || changedValues.schoolYear || changedValues.periodType) {
                        const name = allValues.incomeName || '';
                        let timeStr = '';
                        const currentPeriod = allValues.periodType || addPeriodType;
                        if (currentPeriod === 'year') {
                            if (allValues.schoolYear) {
                                const y = allValues.schoolYear.year();
                                timeStr = ` Năm học ${y}-${y + 1}`;
                            }
                        } else {
                            if (allValues.monthYear) {
                                timeStr = ` ${allValues.monthYear.format('MM/YYYY')}`;
                            }
                        }
                        if (name || timeStr) {
                            form.setFieldsValue({ description: `${name}${timeStr}`.trim() });
                        }
                    }
                }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="type" label="Loại nhóm thu" rules={[{ required: true }]}>
                                <Select placeholder="Chọn loại" onChange={handleIncomeTypeChange}>
                                    <Select.Option value={1}>Nhóm ăn uống</Select.Option>
                                    <Select.Option value={2}>Nhóm học phí</Select.Option>
                                    <Select.Option value={3}>Nhóm học theo buổi (tiết)</Select.Option>
                                    <Select.Option value={4}>Nhóm chi phí khác</Select.Option>
                                    <Select.Option value={5}>Nhóm thu hộ</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="incomeName" label="Tên nguồn thu" rules={[{ required: true }]}>
                                <Select 
                                    placeholder="Chọn tên" 
                                    onChange={handleNameChange}
                                    disabled={!selectedIncomeType}
                                    notFoundContent={!selectedIncomeType ? "Vui lòng chọn Loại nhóm thu trước" : "Không có dữ liệu"}
                                >
                                    {incomeSources.map((source) => (
                                        <Select.Option key={source.id} value={source.name}>{source.name}</Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>
                            <Form.Item name="incomeSchoolId" hidden>
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>

                    {selectedIncomeType && (
                        <>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item name="unitPrice" label="Đơn giá" rules={[{ required: true }]}>
                                        <InputNumber<number>
                                            style={{ width: '100%' }}
                                            min={0}
                                            formatter={(v) => formatCurrency(v as any)}
                                            parser={(v) => parseCurrency(v as any)}
                                            addonAfter={selectedIncomeType === 1 ? "VND/ngày" : selectedIncomeType === 3 ? "VND/buổi" : undefined}
                                        />
                                    </Form.Item>
                                </Col>
                                {selectedIncomeType === 2 && (
                                    <Col span={12}>
                                        <Form.Item name="unitType" label="Đơn vị tính">
                                            <Select
                                                placeholder="Chọn đơn vị tính"
                                                style={{ width: '100%' }}
                                            >
                                                <Select.Option value="month">Theo tháng</Select.Option>
                                                <Select.Option value="semester">Theo kỳ (nửa năm học)</Select.Option>
                                                <Select.Option value="year">Theo năm học</Select.Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                )}
                                {[1, 2, 3].includes(selectedIncomeType) && (
                                    <Col span={12}>
                                        <Form.Item name="expectedUnits" label="Số buổi dự kiến" rules={[{ required: true }]}>
                                            <InputNumber style={{ width: '100%' }} min={1} />
                                        </Form.Item>
                                    </Col>
                                )}
                            </Row>

                            <Row gutter={16}>
                                <Col span={24}>
                                    {renderAddPeriodSelection()}
                                </Col>
                            </Row>

                            <Row gutter={16}>
                                <Col span={24}>
                                    <Form.Item name="description" label="Mô tả" rules={[{ required: true }]}>
                                        <Input />
                                    </Form.Item>
                                </Col>
                            </Row>
                        </>
                    )}
                </Form>

            </Modal>
        </div>
    );
};
